import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import {
  Box,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  List,
  Typography,
} from '@mui/material';
import {
  ICompanyAddViewModel,
  ICompanyViewModel,
  ThirdPartEnum,
} from 'Interfaces/ICompaniesConfig';
import AlphabetSortDrawer from 'components/ui/AlphabetSortDrawer';
import { SingleCompanyView } from 'components/ui/SingleCompanyView';
import accessTokenAtom from 'data/atoms/accessTokenAtom';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { CloudFactoryService } from '../APi/CloudFactoryAPI';
import { ToCompanies } from '../Converts/ToCompanies';
import { ImportCompanyForm } from '../ImportCompanyForm';
import { api } from 'http/apiClient';


interface ICloudFactoryCompanyViewModel {
  name: string;
  phone: string;
  email: string;
  legacyId: number;
  thirdPartyId: string;
  thirdParty: ThirdPartEnum;
  isEmailFoundInBluu: boolean;
  address: {
    street: string;
    area: string;
    postalCode: number | string;
    city: string;
    country: string;
    postBox?: string;
  };
}

interface ICloudFactoryCompanyListViewModel {
  numberOfCloudFactoryCompanies: number;
  cloudFactoryCompanies: ICloudFactoryCompanyViewModel[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectCompany: (company: ICompanyViewModel) => void;
}

export const CloudFactoryCompanyDrawer: React.FC<Props> = ({
  open,
  onClose,
  onSelectCompany,
}) => {
  const { height } = useWindowsDimension();
  const [accessToken] = useAtom(accessTokenAtom);

  const [companies, setCompanies] = useState<ICloudFactoryCompanyViewModel[]>(
    []
  );
  const [company, setCompany] = useState<ICompanyAddViewModel>(
    {} as ICompanyAddViewModel
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredCompanies, setFilteredCompanies] = useState<
    ICloudFactoryCompanyViewModel[]
  >([]);
  const [letterFilter, setLetterFilter] = useState<string | null>(null);

  // Sammenligning mot backend via felles api-klient (med MSAL-token via interceptor)
  const compareMutation = useMutation(
    async (payload: ICloudFactoryCompanyListViewModel) => {
      const response = await api.post<ICloudFactoryCompanyListViewModel>(
        '/Module/CloudFactory/ComparedCompanies',
        payload
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Backend returnerer lista vi skal jobbe videre med
        setCompanies(data.cloudFactoryCompanies);
      },
      onError: (err: any) => {
        setError(err.message || 'Feil ved sammenligning av selskaper');
      },
    }
  );

  const handleMergedCompanySelect = (mergedCompany: ICompanyViewModel) => {
    console.log('handleMergedCompanySelect:', mergedCompany);

    // Fjern valgt selskap fra lista
    setCompanies((prev) =>
      prev.filter((c) => c.thirdPartyId !== mergedCompany.thirdPartyId)
    );

    onSelectCompany(mergedCompany);
  };

  const handleSelectCompany = (c: ICloudFactoryCompanyViewModel) => {
  console.log('handleSelectCompany:', c);

  const selectedCompany: ICompanyAddViewModel = {
    organizationNumber: '',

    name: c.name ?? '',
    phone: c.phone ?? '',
    email: c.email ?? '',

    alias: c.name ?? '',
    supportEmail: null,
    webPage: null,

    companyType: 0 as any,      // TODO: sett riktig EnumCompanyType hvis du vil
    priority: 0 as any,         // TODO: sett riktig EnumPriority hvis du vil

    mainAddress: {
      street: c.address.street,
      area: c.address.area,
      postalCode: c.address.postalCode,
      city: c.address.city,
      country: c.address.country,
      postBox: c.address.postBox || '',
    },

    deliveryAddress: null,
    invoiceAddress: null,

    fileModel: null,

    productBilling: false,
    onHold: false,
    isSupport: false,
    onHoldNote: null,

    creatorId: null,
    fixedHourlyRate: null,
    workRoleId: null,
    contactId: null,

    companyProducts: [],
    productCatalog: [],
    storageProducts: [],
    unassignedCompanyProducts: [],

    legacyId: c.legacyId,
    thirdPartyId: c.thirdPartyId,
    thirdParty: ThirdPartEnum.CloudFactory,
  };

  setCompany(selectedCompany);
};


  // Filtrering på første bokstav
  useEffect(() => {
    let result = companies;
    if (letterFilter) {
      result = result.filter((c) =>
        c.name?.toLowerCase().startsWith(letterFilter.toLowerCase())
      );
    }
    setFilteredCompanies(result);
  }, [companies, letterFilter]);

  // Hent selskaper fra CloudFactory og sammenlign mot backend
  useEffect(() => {
    const fetchAndCompare = async () => {
      if (!open || !accessToken) return;

      setLoading(true);
      setError(null);

      try {
        // 1) Hent fra CloudFactory
        const resp = await CloudFactoryService.getCompanies(
          1,
          250,
          accessToken
        );

        const mapped = ToCompanies(resp.data) as ICompanyAddViewModel[];
        console.log('Fetched companies from CloudFactory:', mapped);

        // 2) Bygg payload til sammenligning
        const comparePayload: ICloudFactoryCompanyListViewModel = {
        numberOfCloudFactoryCompanies: mapped.length,
        cloudFactoryCompanies: mapped.map((c) => {
          return {
            name:
              (c.name ?? '').length > 50
                ? (c.name ?? '').substring(0, 50)
                : c.name ?? '',

            phone: c.phone ?? '',
            email: c.email ?? '',

            legacyId: c.legacyId ?? 0,
            thirdPartyId: c.thirdPartyId ?? '',
            thirdParty: ThirdPartEnum.CloudFactory,
            isEmailFoundInBluu: false,

            address: {
              street: c.mainAddress?.street ?? '',
              area: c.mainAddress?.area ?? '',
              postalCode: c.mainAddress?.postalCode ?? '',
              city: c.mainAddress?.city ?? '',
              country: c.mainAddress?.country ?? 'Norway',
              postBox: c.mainAddress?.postBox ?? '',
            },
          };
        }),
      };


        // 3) Kjør sammenligning mot Bluu-backend
        await compareMutation.mutateAsync(comparePayload);
      } catch (err: any) {
        console.error('Ukjent feil ved henting av selskaper:', err);
        setError(err.message || 'Ukjent feil ved henting av selskaper');
      } finally {
        setLoading(false);
      }
    };

    fetchAndCompare();
  }, [open, accessToken, compareMutation]);

  return (
    <Drawer
      anchor="left"
      open={open}
      variant="persistent"
      PaperProps={{ sx: { width: '100%' } }}
      onClose={onClose}
    >
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Typography
          sx={{
            flexGrow: 1,
            fontWeight: 'bold',
            fontSize: 20,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 1,
            color: 'primary.contrastText',
          }}
        >
          IMPORT COMPANY
        </Typography>
        <IconButton onClick={onClose}>
          <CancelRoundedIcon />
        </IconButton>
      </Box>

      <Grid
        container
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          padding: 1,
        }}
      >
        <Grid item xs={5} sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex' }}>
            <AlphabetSortDrawer
              buttonIcon={<></>}
              buttonContext="companies"
              handleChange={() => {}}
              onLetterChange={(letter: string) => setLetterFilter(letter)}
              fullHeight
            />

            {loading && <CircularProgress />}
            {error && <Typography color="error">{error}</Typography>}

            {!loading && !error && (
              <List
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  height: Math.ceil(height - 128),
                  overflow: 'auto',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                }}
              >
                {filteredCompanies.map((comp, idx) => (
                  <Grid
                    container
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      width: '100%',
                      cursor: 'pointer',
                      padding: 0.5,
                    }}
                    key={comp.thirdPartyId || idx}
                    onClick={() => handleSelectCompany(comp)}
                  >
                    <Grid item xs={12}>
                      <SingleCompanyView
                        index={idx}
                        company={comp}
                        detailNav
                      />
                    </Grid>
                  </Grid>
                ))}
              </List>
            )}
          </Box>
        </Grid>

        <Grid
          item
          xs={7}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: Math.ceil(height - 128),
            overflow: 'auto',
          }}
        >
          <ImportCompanyForm
            expanded
            company={company}
            handleMergedCompanySelect={handleMergedCompanySelect}
          />
        </Grid>
      </Grid>
    </Drawer>
  );
};
